<script setup lang="ts">
import { PrinterIcon } from '@heroicons/vue/outline'
import { DocumentTextIcon } from '@heroicons/vue/solid'
import { yesNoOptions } from '../../../../data'
import { getSelfDisclosureFormUrl } from '../../../../utilities/usePlatformSettings'
import { ecspLegalComplianceProfileConfig } from '../../../account/legal/complianceProfile/ecspComplianceProfile'
import BaseButtonMuted from '../../../base/BaseButtonMuted.vue'
import BaseInputRadio from '../../../base/BaseInputRadio.vue'
import {
    getLegalWidgetFormValidationRules,
    initializeLegalWidgetForm,
    isRequiredLegalComplianceProfileAttribute,
} from '../../ESAC/legal/form/forms'
import { useVModel } from '@vueuse/core'
import { App } from '../../../../typings/api-enums'
import BaseComplianceProfileFileUpload from '../../../base/BaseComplianceProfileFileUpload.vue'
import { computed, watch } from 'vue'

const props = defineProps<{
    modelValue: App.Services.ComplianceProfile.ECSP.Categories.EcspInvestorClassificationDto
}>()
const emit = defineEmits(['update:modelValue'])
const formValues = useVModel(props, 'modelValue', emit)

initializeLegalWidgetForm(
    getLegalWidgetFormValidationRules(
        ecspLegalComplianceProfileConfig['Investor Classification'].validationKey,
        'ecsp',
        ecspLegalComplianceProfileConfig['Investor Classification'].denyKey
    ),
    'ecsp'
)

const treatAsExpertInvestor = computed(() => {
    return formValues.value.expert_investor_treatment === App.Enums.ComplianceProfile.YesNoOption.YES
})

watch(treatAsExpertInvestor, (value) => {
    if (!value) {
        formValues.value.self_disclosure = null
    }
})

function openFile() {
    window.open(getSelfDisclosureFormUrl(), '_blank').focus()
}
</script>

<template>
    <div class="grid grid-cols-1 gap-x-4 gap-y-8 py-3">
        <div class="flex flex-col">
            <BaseInputRadio
                v-model="formValues.expert_investor_treatment"
                name="investor_classification.expert_investor_treatment"
                :options="yesNoOptions"
                :label="$t('ecsp.investor_classification.expert_investor_treatment')"
                :required="
                    isRequiredLegalComplianceProfileAttribute('investor_classification', 'expert_investor_treatment')
                "
                vertical />
            <p class="mt-4">
                {{ $t('ecsp.legal.investor_classification.expert_investor_explanation') }}
            </p>
        </div>
        <div
            :class="{
                'pointer-events-none cursor-not-allowed opacity-50': !treatAsExpertInvestor,
            }">
            <p class="pb-1 text-sm text-gray-700">
                {{ $t('ecsp.investor_classification.self_disclosure_explanation') }}
            </p>
            <div
                class="mb-8 flex items-center overflow-hidden border border-gray-200 bg-white px-2 py-3 shadow sm:col-span-1 sm:rounded-xl sm:px-4">
                <div
                    class="flex min-w-0 flex-1 flex-row items-center justify-between supports-cq:@sm:flex-row supports-not-cq:sm:flex-row">
                    <div class="flex items-center">
                        <div class="flex-shrink-0">
                            <div class="relative flex items-center justify-center rounded-full bg-white p-1">
                                <div class="relative">
                                    <DocumentTextIcon class="h-12 w-12 text-gray-300"></DocumentTextIcon>
                                </div>
                            </div>
                        </div>
                        <div class="min-w-0 flex-1 px-4">
                            <p class="truncate text-sm font-semibold text-gray-900">
                                {{ $t('ecsp.investor_classification.self_disclosure') }}
                            </p>
                        </div>
                    </div>
                    <div class="flex space-x-2">
                        <BaseButtonMuted :icon="PrinterIcon" @click="openFile">
                            {{ $t('ecsp.investor_classification.print') }}
                        </BaseButtonMuted>
                    </div>
                </div>
            </div>
            <div>
                <BaseComplianceProfileFileUpload
                    v-model="formValues.self_disclosure"
                    name="investor_classification.self_disclosure"
                    :label="$t('ecsp.investor_classification.upload_self_disclosure')"
                    :required="treatAsExpertInvestor"
                    :disabled="!treatAsExpertInvestor" />
            </div>
        </div>
    </div>
</template>
